import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PageHeader, ModernCard, SectionHeader } from '../components/ModernUI';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  Settings, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Database,
  Activity,
  Calendar,
  Play,
  History,
  Zap,
  Server,
  FileCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AutomatedImportPage() {
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [testing, setTesting] = useState(false);

  const token = localStorage.getItem('qapi-token');

  const fetchData = useCallback(async () => {
    try {
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [configRes, historyRes, statsRes, providersRes] = await Promise.all([
        fetch(`${API_URL}/api/automated-import/config`, { headers }),
        fetch(`${API_URL}/api/automated-import/history?limit=10`, { headers }),
        fetch(`${API_URL}/api/automated-import/stats`, { headers }),
        fetch(`${API_URL}/api/automated-import/providers`, { headers })
      ]);

      if (configRes.ok) {
        const data = await configRes.json();
        setConfig(data.config);
      }
      if (historyRes.ok) {
        const data = await historyRes.json();
        setHistory(data.history || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      if (providersRes.ok) {
        const data = await providersRes.json();
        setProviders(data.providers || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load import data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConfigUpdate = async (updates) => {
    try {
      const newConfig = { ...config, ...updates };
      
      const response = await fetch(`${API_URL}/api/automated-import/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newConfig)
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data.config);
        toast.success('Configuration updated');
      } else {
        toast.error('Failed to update configuration');
      }
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error('Error updating configuration');
    }
  };

  const handleManualImport = async () => {
    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/automated-import/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source: config?.source || 'wellsky',
          import_census: true,
          import_workforce: true,
          import_clinical: true,
          import_compliance: true
        })
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Import completed: ${result.records_imported} records imported`);
        fetchData();
      } else {
        toast.error(`Import failed: ${result.errors?.join(', ') || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error executing import:', error);
      toast.error('Failed to execute import');
    } finally {
      setImporting(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await fetch(`${API_URL}/api/automated-import/test?source=${config?.source || 'wellsky'}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success('Connection test successful');
      } else {
        toast.error(`Connection test failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'partial':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'in_progress':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      partial: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      pending: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <Badge className={variants[status] || variants.pending}>
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#00b0ab]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="automated-import-page">
      <PageHeader
        icon={Database}
        title="Automated Data Import"
        subtitle="Configure and monitor automated data imports from EHR systems"
        actions={
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing}
              data-testid="test-connection-btn"
            >
              {testing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              Test Connection
            </Button>
            <Button
              onClick={handleManualImport}
              disabled={importing}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="run-import-btn"
            >
              {importing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Import Now
            </Button>
          </div>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ModernCard>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#00b0ab]/15 rounded-lg">
                <Activity className="h-5 w-5 text-[#00b0ab]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Imports</p>
                <p className="text-2xl font-bold">{stats?.stats?.total_imports || 0}</p>
              </div>
            </div>
          </CardContent>
        </ModernCard>

        <ModernCard>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Records Imported</p>
                <p className="text-2xl font-bold">{stats?.stats?.total_records_imported || 0}</p>
              </div>
            </div>
          </CardContent>
        </ModernCard>

        <ModernCard>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Last Import</p>
                <p className="text-sm font-medium">
                  {stats?.last_import ? formatDateTime(stats.last_import) : 'Never'}
                </p>
              </div>
            </div>
          </CardContent>
        </ModernCard>

        <ModernCard>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Next Scheduled</p>
                <p className="text-sm font-medium">
                  {stats?.automation_enabled && stats?.next_scheduled_import
                    ? formatDateTime(stats.next_scheduled_import)
                    : 'Not scheduled'}
                </p>
              </div>
            </div>
          </CardContent>
        </ModernCard>
      </div>

      <Tabs defaultValue="configuration" className="space-y-4">
        <TabsList className="bg-gray-100">
          <TabsTrigger value="configuration" className="data-[state=active]:bg-white">
            <Settings className="h-4 w-4 mr-2" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white">
            <History className="h-4 w-4 mr-2" />
            Import History
          </TabsTrigger>
          <TabsTrigger value="providers" className="data-[state=active]:bg-white">
            <Server className="h-4 w-4 mr-2" />
            EHR Providers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="configuration">
          <ModernCard>
            <CardHeader>
              <CardTitle>Import Configuration</CardTitle>
              <CardDescription>
                Configure automated data imports from your EHR system
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium">Enable Automated Imports</h4>
                  <p className="text-sm text-gray-500">
                    Automatically import data on a scheduled basis
                  </p>
                </div>
                <Switch
                  checked={config?.enabled || false}
                  onCheckedChange={(checked) => handleConfigUpdate({ enabled: checked })}
                  data-testid="enable-imports-toggle"
                />
              </div>

              {/* Source Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Data Source</label>
                  <Select
                    value={config?.source || 'wellsky'}
                    onValueChange={(value) => handleConfigUpdate({ source: value })}
                  >
                    <SelectTrigger data-testid="source-select">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wellsky">WellSky Hospice</SelectItem>
                      <SelectItem value="matrixcare" disabled>MatrixCare (Coming Soon)</SelectItem>
                      <SelectItem value="hchb" disabled>Homecare Homebase (Coming Soon)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Import Frequency</label>
                  <Select
                    value={config?.frequency || 'weekly'}
                    onValueChange={(value) => handleConfigUpdate({ frequency: value })}
                  >
                    <SelectTrigger data-testid="frequency-select">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Day of Week</label>
                  <Select
                    value={config?.day_of_week || 'monday'}
                    onValueChange={(value) => handleConfigUpdate({ day_of_week: value })}
                    disabled={config?.frequency === 'daily' || config?.frequency === 'monthly'}
                  >
                    <SelectTrigger data-testid="day-select">
                      <SelectValue placeholder="Select day" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monday">Monday</SelectItem>
                      <SelectItem value="tuesday">Tuesday</SelectItem>
                      <SelectItem value="wednesday">Wednesday</SelectItem>
                      <SelectItem value="thursday">Thursday</SelectItem>
                      <SelectItem value="friday">Friday</SelectItem>
                      <SelectItem value="saturday">Saturday</SelectItem>
                      <SelectItem value="sunday">Sunday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Import Time</label>
                  <Select
                    value={config?.time || '06:00'}
                    onValueChange={(value) => handleConfigUpdate({ time: value })}
                  >
                    <SelectTrigger data-testid="time-select">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="00:00">12:00 AM</SelectItem>
                      <SelectItem value="06:00">6:00 AM</SelectItem>
                      <SelectItem value="08:00">8:00 AM</SelectItem>
                      <SelectItem value="12:00">12:00 PM</SelectItem>
                      <SelectItem value="18:00">6:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Data Types */}
              <div>
                <h4 className="font-medium mb-3">Data Types to Import</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'import_census', label: 'Census' },
                    { key: 'import_workforce', label: 'Workforce' },
                    { key: 'import_clinical', label: 'Clinical' },
                    { key: 'import_compliance', label: 'Compliance' }
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <Switch
                        checked={config?.[key] ?? true}
                        onCheckedChange={(checked) => handleConfigUpdate({ [key]: checked })}
                        data-testid={`${key}-toggle`}
                      />
                      <span className="text-sm">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>

        <TabsContent value="history">
          <ModernCard>
            <CardHeader>
              <CardTitle>Import History</CardTitle>
              <CardDescription>Recent data import jobs and their status</CardDescription>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No import history yet</p>
                  <p className="text-sm">Run your first import to see history here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((item) => (
                    <div
                      key={item.import_id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      data-testid={`import-${item.import_id}`}
                    >
                      <div className="flex items-center gap-3">
                        {getStatusIcon(item.status)}
                        <div>
                          <p className="font-medium">{item.import_id}</p>
                          <p className="text-sm text-gray-500">
                            {item.import_type} • {item.source} • {formatDateTime(item.started_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium">{item.records_imported} records</p>
                          <p className="text-xs text-gray-500">imported</p>
                        </div>
                        {getStatusBadge(item.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        <TabsContent value="providers">
          <ModernCard>
            <CardHeader>
              <CardTitle>EHR Providers</CardTitle>
              <CardDescription>Supported electronic health record integrations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {providers.map((provider) => (
                  <div
                    key={provider.id}
                    className={`p-4 rounded-lg border-2 ${
                      provider.status === 'available'
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                    data-testid={`provider-${provider.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium">{provider.name}</h4>
                      <Badge
                        className={
                          provider.status === 'available'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }
                      >
                        {provider.status === 'available' ? 'Available' : 'Coming Soon'}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">
                        Auth: {provider.auth_type}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {provider.features?.map((feature) => (
                          <Badge key={feature} variant="outline" className="text-xs">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
